import { useState } from 'react'

import type { ColumnDef } from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'

import BackIcon from '@/assets/icons/icon-back.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx'
import { Button } from '@/components/ui/button.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { Tag } from '@/components/ui/tag.tsx'
import { useSocialAccounts } from '@/modules/statistics/social/hooks/use-social-accounts.ts'
import { SocialMediaTypeProvider } from '@/modules/statistics/social/hooks/use-social-media-type.tsx'
import type { SocialAccount } from '@/modules/statistics/social/types'
import {
  SocialAccountStatus,
  SocialMediaType,
} from '@/modules/statistics/social/types'
import { AccountAction } from '@/modules/statistics/social/ui/Accounts/AccountAction'
import { AccountAddDialog } from '@/modules/statistics/social/ui/Accounts/Dialogs/account-add-dialog.tsx'
import { TeamPageSkeleton } from '@/modules/team/ui/team-page-skeleton'

const columns: ColumnDef<SocialAccount>[] = [
  {
    header: 'Instagram Username',
    accessorKey: 'name',
    cell: ({ row }) => {
      const { name, avatarUrl } = row.original

      return (
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            {avatarUrl ? (
              <AvatarImage
                src={avatarUrl}
                alt={name}
              />
            ) : (
              <AvatarFallback className="bg-secondary-50 dark:bg-secondary-300">
                {name?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            )}
          </Avatar>
          <a
            href={`https://instagram.com/${name}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary-500 hover:underline"
          >
            {name}
          </a>
        </div>
      )
    },
  },
  {
    header: 'Status',
    id: 'status',
    accessorKey: 'status',
    cell: ({ row }) => {
      const status = row.getValue('status') as string

      const mapStatus = (originalStatus: string) => {
        switch (originalStatus) {
          case SocialAccountStatus.Disabled:
            return 'Inactive'
          case SocialAccountStatus.VerificationDenied:
            return 'Not owned by creator'
          case SocialAccountStatus.Active:
            return 'Active'
        }
      }

      return (
        <Tag
          withDot
          variant={status === 'Disabled' ? 'danger' : 'success'}
        >
          {mapStatus(status)}
        </Tag>
      )
    },
  },
  {
    id: 'actions',
    header: () => <div className="w-full text-right">Action</div>,
    cell: ({ row }) => <AccountAction row={row} />,
  },
]

export const InstagramAccountsPage = () => {
  const { data, isFetching } = useSocialAccounts(SocialMediaType.Instagram)

  const [showAddDialog, setShowAddDialog] = useState(false)

  const redirect = useNavigate()

  const openAddDialog = () => {
    setShowAddDialog(true)
  }

  if (!data || isFetching) {
    return <TeamPageSkeleton />
  }

  return (
    <SocialMediaTypeProvider socialMediaType={SocialMediaType.Instagram}>
      <div className="w-full px-6 py-7 lg:px-12">
        <header className="flex items-end justify-between">
          <div className="flex flex-col gap-3">
            <div
              onClick={() => redirect(-1)}
              className="flex cursor-pointer items-center gap-2 text-sm font-medium text-primary-500"
            >
              <BackIcon />
              <span>Back to Instagram Reels Statistics</span>
            </div>
            <h1 className="text-3xl font-semibold text-title">
              Instagram Accounts
            </h1>
          </div>
          <Button
            className="h-fit w-fit"
            onClick={openAddDialog}
          >
            <PlusIcon />
            Add Account
          </Button>
        </header>

        <div className="mt-6">
          <DataTable
            enableSorting
            data={data}
            columns={columns}
          />
        </div>
      </div>

      <AccountAddDialog
        setOpen={setShowAddDialog}
        open={showAddDialog}
      />
    </SocialMediaTypeProvider>
  )
}
